import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../api/client';
import { postWithRetry } from '../lib/authErrors';
import { logger } from '../lib/logger';

const AuthCtx = createContext(null);

// Auth token lives in an httpOnly cookie; only the non-sensitive profile is cached for fast first paint.
const persistUser = (setUser, userObj) => {
    localStorage.setItem('cds_user', JSON.stringify(userObj));
    setUser(userObj);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => {
        try {
            const raw = localStorage.getItem('cds_user');
            return raw ? JSON.parse(raw) : null;
        } catch (e) {
            logger.warn('cached user parse failed', e);
            return null;
        }
    });
    const [loading, setLoading] = useState(false);

    const login = useCallback(async (email, password) => {
        const { data } = await postWithRetry(apiClient, '/auth/login', { email, password });
        persistUser(setUser, data.user);
        return data.user;
    }, []);

    const register = useCallback(async (email, password, name) => {
        const { data } = await postWithRetry(apiClient, '/auth/register', { email, password, name });
        persistUser(setUser, data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiClient.post('/auth/logout');
        } catch (e) {
            logger.warn('logout request failed — clearing local user anyway', e);
        }
        localStorage.removeItem('cds_user');
        setUser(null);
    }, []);

    useEffect(() => {
        // Gate the /auth/me probe on having a cached user — avoids a noisy 401
        // on the public /login and /signup pages.
        const cached = localStorage.getItem('cds_user');
        if (!cached) return;
        apiClient
            .get('/auth/me')
            .then(({ data }) => {
                localStorage.setItem('cds_user', JSON.stringify(data));
                setUser(data);
            })
            .catch(() => {
                localStorage.removeItem('cds_user');
                setUser(null);
            });
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, logout }),
        [user, loading, login, register, logout]
    );

    return (
        <AuthCtx.Provider value={value}>
            {children}
        </AuthCtx.Provider>
    );
};

export const useAuth = () => useContext(AuthCtx);
